// Shared grade-band column rules so Data Entry, Student Profiles and Family Progress
// show a grade-band standard (e.g. 9-12) once in its own column instead of in every grade.
export const GRADE_SEQUENCE = ['PK', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

export function normalizeGradeValue(gradeValue) {
  return String(gradeValue ?? '').toUpperCase().trim()
    .replace(/^GRADES?\s+/, '')
    .replace(/^([0-9]+|PK|K)\s+TO\s+([0-9]+)$/i, '$1-$2')
    .replace(/\s*-\s*/, '-');
}

export function expandGradeRange(gradeValue) {
  const normalized = normalizeGradeValue(gradeValue);
  if (!normalized.includes('-')) return [normalized];
  const [startRaw, endRaw] = normalized.split('-').map((part) => part.trim());
  const startIndex = GRADE_SEQUENCE.indexOf(startRaw);
  const endIndex = GRADE_SEQUENCE.indexOf(endRaw);
  if (startIndex === -1 || endIndex === -1 || startIndex > endIndex) return [normalized];
  return GRADE_SEQUENCE.slice(startIndex, endIndex + 1);
}

// Band standards can be tagged by grade level ("9-12") or only by code prefix ("3-5-ETS1-1", "9-12.X").
export function getStandardApplicableGrades(gradeValue, standardCode = '') {
  const codeGrade = String(standardCode || '').trim().toUpperCase()
    .match(/^(PK|K|[1-9]|1[0-2])(?:-(PK|K|[1-9]|1[0-2]))?(?=[.-])/);
  const rangeGrade = codeGrade?.[2] ? `${codeGrade[1]}-${codeGrade[2]}` : '';
  return expandGradeRange(rangeGrade || gradeValue);
}

export function getGradeColumnKey(applicableGrades = []) {
  if (applicableGrades.length > 1) return `${applicableGrades[0]}-${applicableGrades[applicableGrades.length - 1]}`;
  return applicableGrades[0] || '';
}

export function getStandardGradeColumnKey(gradeValue, standardCode = '') {
  return getGradeColumnKey(getStandardApplicableGrades(gradeValue, standardCode));
}

function toColumn(key, grades) {
  const ranks = grades.map((grade) => GRADE_SEQUENCE.indexOf(grade));
  return { key, grades, start: Math.min(...ranks), end: Math.max(...ranks), isBand: grades.length > 1 };
}

export function sortGradeColumns(columns) {
  // Single grades sit before a band that ends on the same grade, so 6, 7, 8 come before 6-8 and 6-8 before 9-12.
  return [...columns].sort((left, right) => left.end - right.end || right.start - left.start);
}

export function buildGradeColumns(rows = [], { includeUncoveredGrades = false } = {}) {
  const columns = new Map();
  rows.forEach((row) => {
    const grades = row.applicableGrades || [];
    const key = getGradeColumnKey(grades);
    if (!key || columns.has(key)) return;
    columns.set(key, toColumn(key, grades));
  });
  if (includeUncoveredGrades) {
    const bands = Array.from(columns.values()).filter((column) => column.isBand);
    GRADE_SEQUENCE.forEach((grade) => {
      if (columns.has(grade) || bands.some((band) => band.grades.includes(grade))) return;
      columns.set(grade, toColumn(grade, [grade]));
    });
  }
  return sortGradeColumns(Array.from(columns.values()));
}

export function findGradeColumnIndex(columns, grade) {
  const exact = columns.findIndex((column) => !column.isBand && column.key === grade);
  if (exact !== -1) return exact;
  let best = -1;
  columns.forEach((column, index) => {
    if (!column.grades.includes(grade)) return;
    if (best === -1 || column.grades.length < columns[best].grades.length) best = index;
  });
  return best;
}

export function formatGradeColumnLabel(grade) {
  const normalized = String(grade || '').toUpperCase().trim();
  return normalized.includes('-') ? `Grades ${normalized}` : `Grade ${normalized}`;
}
